import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { AlertCircle } from "lucide-react";
import type { FieldErrors } from "react-hook-form";
import { getFieldPathLabel, humanizeFieldMessage } from "@/lib/errorMessages";
import { flattenFieldErrors } from "@ckgo2/shared";

/**
 * Client-side (Zod) validation errors of an edit form, rendered as a banner.
 *
 * `ErrorBanner` only shows what the SERVER said — but when the resolver rejects, no request
 * is ever made: `handleSubmit` swallows the failure and the page looks idle. Fields whose
 * error has no inline `<p>` (MediaPicker-driven ids, conditionally rendered inputs, anything
 * the user cannot even see) then make saving impossible with zero feedback. This banner is
 * the catch-all so a rejected submit is always visible.
 */
interface ValidationErrorBannerProps {
  errors: FieldErrors;
}

export default function ValidationErrorBanner({ errors }: ValidationErrorBannerProps) {
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);
  const flat = flattenFieldErrors(errors, { skipServer: true });

  useEffect(() => {
    if (flat.length > 0 && ref.current) {
      ref.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
    // Re-scroll only when the set of failing fields changes, not on every render.
  }, [flat.map((e) => e.path).join("|")]); // eslint-disable-line react-hooks/exhaustive-deps

  if (flat.length === 0) return null;

  return (
    <div
      ref={ref}
      role="alert"
      className="bg-error-50 dark:bg-error-900/30 rounded-lg border border-red-200 p-4 dark:border-red-800/50"
    >
      <div className="flex items-start gap-3">
        <AlertCircle className="text-error-600 dark:text-error-400 mt-0.5 h-5 w-5 shrink-0" />
        <div className="min-w-0">
          <p className="text-error-700 dark:text-error-400 font-medium">
            {t("shared.validationBanner.title")}
          </p>
          <ul className="text-error-600 dark:text-error-400/80 mt-2 space-y-1 text-sm">
            {flat.map((e) => (
              <li key={e.path}>
                <span className="font-medium">{getFieldPathLabel(e.path)}:</span>{" "}
                {humanizeFieldMessage(e.message)}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}
